import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import type { CycleCreate, CycleUpdate, Cycle } from '../models/cycle.types';

/**
 * Simplified Cycle Service - Cycles are just temporal groupers
 * No state management, no complex validations
 */
class CycleServiceClass {
  private collectionName = 'cycles';

  /**
   * Create a new cycle
   */
  async create(data: CycleCreate): Promise<string> {
    const startDate = data.startDate instanceof Timestamp ? data.startDate : Timestamp.fromDate(data.startDate as any);
    const endDate = data.endDate instanceof Timestamp ? data.endDate : Timestamp.fromDate(data.endDate as any);

    if (startDate.toMillis() >= endDate.toMillis()) {
      throw new Error('okr/invalid-dates: Data de início deve ser anterior à data de término');
    }

    const docRef = await addDoc(collection(db, this.collectionName), {
      name: data.name,
      description: data.description || '',
      startDate,
      endDate,
      createdBy: data.createdBy,
      deleted: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return docRef.id;
  }

  /**
   * Update an existing cycle
   */
  async update(id: string, data: Partial<CycleUpdate>): Promise<void> {
    const cycleDoc = await getDoc(doc(db, this.collectionName, id));
    if (!cycleDoc.exists()) {
      throw new Error('okr/cycle-not-found: Ciclo não encontrado');
    }

    const currentData = cycleDoc.data();

    // Convert dates to Timestamp if needed
    const startDate = data.startDate
      ? (data.startDate instanceof Timestamp ? data.startDate : Timestamp.fromDate(data.startDate as any))
      : currentData.startDate;
    const endDate = data.endDate
      ? (data.endDate instanceof Timestamp ? data.endDate : Timestamp.fromDate(data.endDate as any))
      : currentData.endDate;

    if (startDate.toMillis() >= endDate.toMillis()) {
      throw new Error('okr/invalid-dates: Data de início deve ser anterior à data de término');
    }

    const updateData: any = {
      ...(data.name && { name: data.name }),
      ...(data.description !== undefined && { description: data.description }),
      ...(data.startDate && { startDate }),
      ...(data.endDate && { endDate }),
      updatedAt: serverTimestamp(),
    };

    await updateDoc(doc(db, this.collectionName, id), updateData);
  }

  /**
   * Delete a cycle (soft delete)
   */
  async close(id: string): Promise<void> {
    await updateDoc(doc(db, this.collectionName, id), {
      deleted: true,
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Delete a cycle (soft delete) - alias for close()
   */
  async delete(id: string): Promise<void> {
    return this.close(id);
  }

  /**
   * Get all cycles (excluding deleted)
   */
  async getAll(): Promise<Cycle[]> {
    const q = query(
      collection(db, this.collectionName),
      where('deleted', '!=', true),
      orderBy('deleted'),
      orderBy('startDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => this.formatCycle(doc.id, doc.data()));
  }

  /**
   * Get active cycles (for backwards compatibility with useCyclesQuery(true))
   */
  async getActive(): Promise<Cycle[]> {
    // Since we removed status, just return all cycles
    return this.getAll();
  }

  /**
   * Get cycle by ID
   */
  async getById(id: string): Promise<Cycle | null> {
    const docSnap = await getDoc(doc(db, this.collectionName, id));
    if (!docSnap.exists()) return null;
    return this.formatCycle(docSnap.id, docSnap.data());
  }

  /**
   * Subscribe to real-time updates
   */
  subscribeToCycles(callback: (cycles: Cycle[]) => void): () => void {
    const q = query(
      collection(db, this.collectionName),
      where('deleted', '!=', true),
      orderBy('deleted'),
      orderBy('startDate', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const cycles = snapshot.docs.map((doc) => this.formatCycle(doc.id, doc.data()));
      callback(cycles);
    });

    return unsubscribe;
  }

  /**
   * Format Firestore data to Cycle type
   */
  private formatCycle(id: string, data: any): Cycle {
    return {
      id,
      name: data.name,
      description: data.description || undefined,
      startDate: this.toTimestamp(data.startDate),
      endDate: this.toTimestamp(data.endDate),
      createdBy: data.createdBy,
      createdAt: this.toTimestamp(data.createdAt),
      updatedAt: this.toTimestamp(data.updatedAt),
      _metadata: data._metadata,
    };
  }

  /**
   * Ensure value is a Firestore Timestamp
   */
  private toTimestamp(value: unknown): Timestamp {
    if (value instanceof Timestamp) return value;
    if (value instanceof Date) return Timestamp.fromDate(value);
    return Timestamp.now();
  }
}

export const cycleService = new CycleServiceClass();
